<script lang="ts">
	import AdvocacyIcon from '$src/lib/components/icons/AdvocacyIcon.svelte'
	import AuthenticityIcon from '$src/lib/components/icons/AuthenticityIcon.svelte'
	import EmpowermentIcon from '$src/lib/components/icons/EmpowermentIcon.svelte'
	import InnovationIcon from '$src/lib/components/icons/InnovationIcon.svelte'
	import SustainabilityIcon from '$src/lib/components/icons/SustainabilityIcon.svelte'
	import TransparencyIcon from '$src/lib/components/icons/TransparencyIcon.svelte'

	// TypeScript type for the social link
	type SocialLink = {
		name: string
		icon: string // Assuming you have SVG icons as strings
		url: string
	}

	// Define your social links
	const socialLinks: SocialLink[] = [
		{ name: 'Instagram', icon: '/path/to/instagram.svg', url: 'https://instagram.com/' },
		{ name: 'GitHub', icon: '/path/to/github.svg', url: 'https://github.com/' },
		{ name: 'LinkedIn', icon: '/path/to/linkedin.svg', url: 'https://linkedin.com/' },
	]
</script>

<div class="bg-brand-gray-5">
	<main class="relative isolate mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
		<!-- Background -->
		<div
			class="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
			aria-hidden="true"
		>
			<div
				class="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-brand-primary-25 to-brand-primary opacity-25"
				style="clip-path: polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)"
			></div>
		</div>

		<!-- Header section -->
		<div class="px-6 pt-14 lg:px-8">
			<div class="mx-auto max-w-2xl pt-24 text-center sm:pt-40">
				<h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">We ❤️ Creators</h1>
				<h3 class="mt-6 text-xl lg:text-2xl text-pretty leading-7 text-brand-gray-1 font-normal">
					Our platform is meticulously designed to empower creators, giving them not just a stage,
					but a supportive community that values authenticity, sustainability, and transformative
					experiences. By prioritizing the needs and voices of creators, Airheart strives to reshape
					the travel industry into a space where passion meets purpose.
				</h3>
			</div>
		</div>

		<!-- Content section -->
		<div class="mx-auto mt-20 max-w-7xl px-6 lg:px-8" data-testid="StatsSection">
			<div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
				<div
					class="grid text-pretty max-w-xl grid-cols-1 gap-8 text-base leading-7 text-brand-gray-1 lg:max-w-none lg:grid-cols-2"
				>
					<div>
						<p>
							In a world teeming with wonder, creators are the vanguards of meaningful and mindful
							exploration. With every step they take, they weave stories of adventure, combining the
							thrill of discovery with the wisdom of experience. As they carve out paths in
							uncharted territories, they don't just guide us through the world's wonders; they
							usher us into a journey of self-discovery, helping us find passions that resonate and
							experiences that rejuvenate.
						</p>
						<p class="mt-8">
							Every tale spun by a creator is more than just a narrative; it's an invitation. An
							invitation to break free from the confines of daily grind, to rise above the cacophony
							of modern life's stressors. Through their eyes, we're reminded of the serenity of
							untouched nature, the vibrancy of bustling cities, and the symphony of diverse
							cultures intertwining. They guide us to not just see, but to immerse, to feel the
							pulse of places, ensuring that our travels transcend mere sightseeing and morph into
							soulful sojourns.
						</p>
					</div>
					<div>
						<p>
							Yet, the paths they illuminate are tread with care and consciousness. For creators are
							not just explorers, but guardians of our planet's fragility. In showcasing the majesty
							of nature and the intricacies of cultures, they simultaneously emphasize the
							importance of sustainable travel. Their journeys highlight the balance between
							marveling at the world's beauty and preserving it for generations to come. By
							championing eco-friendly practices, they inspire travelers to embark on adventures
							that celebrate the world without compromising it.
						</p>
						<p class="mt-8">
							As we follow these trailblazers, we're not just navigating global terrains but our own
							inner landscapes. Travel, as creators show us, is more than movement from one place to
							another; it's a transformative experience, a salve for our spirits. It offers an
							escape, yes, but more than that, a reconnection: to the world, to others, and to our
							own untapped passions. In this dance of discovery and reflection, creators are our
							guides, ensuring every trip becomes a journey of the heart and soul.
						</p>
					</div>
				</div>
				<dl
					class="mt-16 grid grid-cols-1 gap-x-8 gap-y-12 sm:mt-20 sm:grid-cols-2 sm:gap-y-16 lg:mt-28 xl:mt-36 lg:grid-cols-4"
				>
					<div class="flex flex-col-reverse gap-y-3 border-l border-white/40 pl-6">
						<dt class="text-base leading-7 text-brand-gray-3">Business was founded</dt>
						<dd class="text-3xl font-semibold tracking-tight text-white">2021</dd>
					</div>
					<div class="flex flex-col-reverse gap-y-3 border-l border-white/40 pl-6">
						<dt class="text-base leading-7 text-brand-gray-3">Guides for sale</dt>
						<dd class="text-3xl font-semibold tracking-tight text-white">20+</dd>
					</div>
					<div class="flex flex-col-reverse gap-y-3 border-l border-white/40 pl-6">
						<dt class="text-base leading-7 text-brand-gray-3">Travelers served</dt>
						<dd class="text-3xl font-semibold tracking-tight text-white">6M+</dd>
					</div>
					<div class="flex flex-col-reverse gap-y-3 border-l border-white/40 pl-6">
						<dt class="text-base leading-7 text-brand-gray-3">Travelers on the email list</dt>
						<dd class="text-3xl font-semibold tracking-tight text-white">105k+</dd>
					</div>
				</dl>
			</div>
		</div>

		<!-- Image section -->
		<div class="mt-32 sm:mt-40 lg:mt-48 xl:mx-auto xl:max-w-7xl xl:px-8" data-testid="ImageSection">
			<div class="flex items-center flex-col lg:flex-row lg:space-x-4 w-full">
				<div class="flex items-start flex-col w-full lg:w-1/2 space-y-4">
					<img
						src="/visuals/stock/about.webp"
						alt=""
						class="w-full object-cover rounded-2xl xl:rounded-3xl"
					/>
					<img
						src="/visuals/stock/about3.webp"
						alt=""
						class="w-full object-cover rounded-2xl xl:rounded-3xl"
					/>
				</div>
				<img
					src="/visuals/stock/about2.webp"
					alt=""
					class="w-full lg:w-1/2 object-cover rounded-2xl xl:rounded-3xl"
				/>
			</div>
			<p class="text-brand-gray-2 mt-4 ml-1 text-sm">Airheart Launch Party, June 2024</p>
		</div>

		<!-- Values section -->
		<div class="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:mt-48 lg:px-8" data-testid="ValuesSection">
			<div class="mx-auto max-w-2xl lg:mx-0">
				<h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">Values</h2>
				<p class="mt-6 text-base text-pretty leading-8 text-brand-gray-3">
					At Airheart, our deeply ingrained values are more than just words; they are our guiding
					principles. We take them seriously, ensuring they influence every decision and action,
					underlining our commitment to both creators and travelers.
				</p>
			</div>
			<dl
				class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base leading-7 text-brand-gray-3 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:gap-x-16"
			>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<AuthenticityIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>

						Authenticity
					</dt>
					<dd class="inline text-pretty">
						At Airheart, we believe in genuine experiences, authentic storytelling, and being true
						to our mission. Every journey should be as unique as the traveler, and our platform is
						here to facilitate that.
					</dd>
				</div>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<EmpowermentIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>
						Empowerment
					</dt>
					<dd class="inline text-pretty">
						We aim to uplift both creators and travelers, giving them the tools, knowledge, and
						platform to craft their narratives, choose their paths, and empower their communities.
					</dd>
				</div>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<InnovationIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>
						Innovation
					</dt>
					<dd class="inline text-pretty">
						As the travel landscape changes, Airheart is committed to embracing new ideas,
						technologies, and approaches to enhance the travel experience.
					</dd>
				</div>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<AdvocacyIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>

						Advocacy
					</dt>
					<dd class="inline text-pretty">
						We champion the rights and needs of our community. From promoting sustainable travel to
						ensuring creators are recognized and compensated fairly, advocacy is at our core.
					</dd>
				</div>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<TransparencyIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>
						Transparency
					</dt>
					<dd class="inline text-pretty">
						In a world filled with fine print and hidden agendas, Airheart pledges clarity. We
						promise to be transparent in our dealings, keeping our community informed and engaged.
					</dd>
				</div>
				<div class="relative pl-9 flex flex-col text-sm">
					<dt class="inline font-semibold text-white">
						<SustainabilityIcon
							width={'20'}
							height={'20'}
							class="absolute left-1 top-1 h-5 w-5 text-brand-primary"
						/>
						Sustainable Exploration
					</dt>
					<dd class="inline text-pretty">
						Understanding the environmental impact of travel, we're dedicated to promoting and
						supporting more sustainable modes of transportation and tourism. We believe in spreading
						the joys of travel beyond the usual, often overcrowded spots, bringing benefits to
						communities that genuinely wish for them.
					</dd>
				</div>
			</dl>
		</div>

		<!-- Timeline section -->
		<div
			class="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:mt-48 lg:px-8"
			data-testid="TimelineSection"
		>
			<div class="mx-auto max-w-2xl lg:mx-0">
				<h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">History</h2>
				<p class="mt-6 text-base text-pretty leading-8 text-brand-gray-3">
					Born amidst the tumultuous waves of pandemic travel, Airheart swiftly became the digital
					compass for millions. Melding the aspirations of today's travelers with the expertise of
					digital creators, we've embarked on a journey to redefine travel experiences. With strong
					industry partnerships and a pioneering spirit, we're shaping the future of globetrotting
					one step at a time.
				</p>
			</div>
			<div
				class="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-4"
			>
				<div>
					<time
						datetime="2021-08"
						class="flex items-center text-sm font-semibold leading-6 text-brand-primary"
					>
						<svg viewBox="0 0 4 4" class="mr-4 h-1 w-1 flex-none" aria-hidden="true">
							<circle cx="2" cy="2" r="2" fill="currentColor" />
						</svg>
						Mar 2021
						<div
							class="absolute -ml-2 h-px w-screen -translate-x-full bg-brand-gray-4/40 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
							aria-hidden="true"
						></div>
					</time>
					<p class="mt-6 text-sm font-semibold leading-8 tracking-tight text-brand-gray-3">
						Founded company
					</p>
					<p class="mt-1 text-sm text-pretty leading-6 text-brand-gray-3">
						Airheart was founded on the vision of seamlessly connecting the aspirations of modern
						travelers with the expertise of digital creators.
					</p>
				</div>
				<div>
					<time
						datetime="2021-12"
						class="flex items-center text-sm font-semibold leading-6 text-brand-primary"
					>
						<svg viewBox="0 0 4 4" class="mr-4 h-1 w-1 flex-none" aria-hidden="true">
							<circle cx="2" cy="2" r="2" fill="currentColor" />
						</svg>
						May 2021
						<div
							class="absolute -ml-2 h-px w-screen -translate-x-full bg-brand-gray-4/40 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
							aria-hidden="true"
						></div>
					</time>
					<p class="mt-6 text-sm font-semibold leading-8 tracking-tight text-brand-gray-3">
						Launched Travel Bans Explorer
					</p>
					<p class="mt-1 text-sm text-pretty leading-6 text-brand-gray-3">
						Airheart introduced the Travel Bans Explorer, pioneering a simple solution for travelers
						navigating pandemic-era restrictions.
					</p>
				</div>
				<div>
					<time
						datetime="2022-02"
						class="flex items-center text-sm font-semibold leading-6 text-brand-primary"
					>
						<svg viewBox="0 0 4 4" class="mr-4 h-1 w-1 flex-none" aria-hidden="true">
							<circle cx="2" cy="2" r="2" fill="currentColor" />
						</svg>
						June 2022
						<div
							class="absolute -ml-2 h-px w-screen -translate-x-full bg-brand-gray-4/40 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
							aria-hidden="true"
						></div>
					</time>
					<p class="mt-6 text-sm font-semibold leading-8 tracking-tight text-brand-gray-3">
						Raised 325k pre-seed
					</p>
					<p class="mt-1 text-sm text-pretty leading-6 text-brand-gray-3">
						In a testament to our potential and vision, we successfully secured our pre-seed funding
						round.
					</p>
				</div>
				<div>
					<time
						datetime="2022-12"
						class="flex items-center text-sm font-semibold leading-6 text-brand-primary"
					>
						<svg viewBox="0 0 4 4" class="mr-4 h-1 w-1 flex-none" aria-hidden="true">
							<circle cx="2" cy="2" r="2" fill="currentColor" />
						</svg>
						Q3 2024
						<div
							class="absolute -ml-2 h-px w-screen -translate-x-full bg-brand-gray-4/40 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
							aria-hidden="true"
						></div>
					</time>
					<p class="mt-6 text-sm font-semibold leading-8 tracking-tight text-brand-gray-3">
						Launched Airheart
					</p>
					<p class="mt-1 text-sm text-pretty leading-6 text-brand-gray-3">
						The digital travel landscape is set to shift with Airheart's much-anticipated Q3 launch.
					</p>
				</div>
			</div>
		</div>

		<!-- Team section -->
		<div
			class="relative mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:mt-48 lg:px-8"
			data-testid="TeamSection"
		>
			<div
				class="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
				aria-hidden="true"
			>
				<div
					class="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-brand-primary-50 to-brand-gray-0 opacity-25"
					style="clip-path: polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)"
				></div>
			</div>
			<div class="mx-auto max-w-2xl lg:max-w-none lg:mx-0 lg:flex items-center gap-x-16">
				<div class="w-full">
					<h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">Founder</h2>
					<p class="mt-6 text-base text-pretty leading-8 text-brand-gray-3">
						Lindsey Renken's journey is not just about traveling to over 30 countries and 200+
						destinations or her pursuits in entrepreneurship. It's also a story of tenacity, vision,
						and innovation. As a solo startup founder, she combined her vast expertise in software
						development and marketing with her firsthand experiences as a global wanderer over the
						past decade.
					</p>
					<p class="mt-6 text-base text-pretty leading-8 text-brand-gray-3">
						Every detail in her retired travel blog testifies to her deep connection with varied
						cultures and places. Lindsey's unique stance as a solo founder has sculpted Airheart's
						core principles. With an unwavering commitment to lean and transparent operations, she
						champions the power of automation and AI.
					</p>
					<p class="mt-6 text-base text-pretty leading-8 text-brand-gray-3">
						Her mission? To ensure creators are adequately compensated and that every traveler finds
						their journey enriched.
					</p>
				</div>

				<img
					class="aspect-[7/5] mt-12 lg:-mr-24 lg:mt-0 w-2/3 md:w-1/2 mx-auto lg:w-1/4 z-10 rounded-2xl -rotate-2 object-cover"
					src="/visuals/stock/lindsey-travel.webp"
					alt="Lindsey Renken Hiking in Tasmania"
				/>
				<img
					class="aspect-[11/13] lg:mt-0 w-2/3 md:w-1/2 lg:w-1/4 mx-auto rounded-2xl rotate-6 object-cover"
					src="/visuals/stock/lindsey-profile.webp"
					alt="Headshot of Lindsey Renken"
				/>

				<!-- <div class="bg-gray-800 text-white p-5">
						<ul>
							{#each socialLinks as link}
								<li class="flex items-center mt-4 first:mt-0">
									<img src={link.icon} alt={link.name} class="h-6 w-6" />
									<a href={link.url} class="ml-3 text-sm hover:underline">
										Follow on {link.name}
									</a>
								</li>
							{/each}
						</ul>
					</div> -->
			</div>
		</div>

		<div class="relative mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:mt-48 lg:px-8">
			<div
				class="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
				aria-hidden="true"
			>
				<div
					class="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-l from-brand-primary-50 to-brand-gray-0 opacity-25"
					style="clip-path: polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)"
				></div>
			</div>
			<div class="mx-auto max-w-2xl lg:mx-0">
				<h2
					class="text-3xl font-bold tracking-tight text-white sm:text-4xl"
					data-testid="InvestorsSection"
				>
					Investors
				</h2>
				<p class="mt-6 text-base leading-8 text-brand-gray-3">
					At Airheart, we don't just seek investors; we cultivate partnerships. Our select group of
					backers resonates deeply with our mission and values. Together, we're not just fueling a
					company; we're nurturing a shared dream. Their trust and alignment empower us to redefine
					travel and creator interactions for the better.
				</p>
				<p class="mt-6 text-base leading-8 text-brand-gray-3">
					We'd love to work with female-led firms and angel investors. If you're interested in,
					please reach out.
				</p>
			</div>
			<ul
				role="list"
				class="mx-auto py-20 lg:pb-48 xl:pb-64 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-4 xl:grid-cols-4"
			>
				<li class="flex flex-col items-center justify-center w-full">
					<img class="w-2/3 rounded-full object-cover" src="/visuals/stock/josh.webp" alt="" />
					<h3 class="mt-6 text-lg font-semibold leading-8 tracking-tight text-white">
						Josh Kaladermis
					</h3>
					<p class="text-sm leading-7 text-brand-gray-3">Co-Founder of TravisCI</p>
					<p class="text-xs leading-6 text-brand-gray-3">New Zealand</p>
				</li>

				<li class="flex flex-col items-center justify-center w-full">
					<img class="w-2/3 rounded-full object-cover" src="/visuals/stock/ben.webp" alt="" />
					<h3 class="mt-6 text-lg font-semibold leading-8 tracking-tight text-white">Ben Lamm</h3>
					<p class="text-sm leading-7 text-brand-gray-3">Co-Founder of Colossal Biosciences</p>
					<p class="text-xs leading-6 text-brand-gray-3">United States</p>
				</li>

				<li class="flex flex-col items-center justify-center w-full">
					<img class="w-2/3 rounded-full object-cover" src="/visuals/stock/andrew.webp" alt="" />
					<h3 class="mt-6 text-lg font-semibold leading-8 tracking-tight text-white">
						Andrew Busey
					</h3>
					<p class="text-sm leading-7 text-brand-gray-3">Co-Founder of Colossal Biosciences</p>
					<p class="text-xs leading-6 text-brand-gray-3">United States</p>
				</li>

				<li class="flex flex-col items-center justify-center w-full">
					<img class="w-2/3 rounded-full object-cover" src="/visuals/stock/john.webp" alt="" />
					<h3 class="mt-6 text-lg font-semibold leading-8 tracking-tight text-white">
						John Servinis
					</h3>
					<p class="text-sm leading-7 text-brand-gray-3">Co-Founder of HotDoc</p>
					<p class="text-xs leading-6 text-brand-gray-3">Australia</p>
				</li>
			</ul>
		</div>
	</main>
</div>
